import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Lock, User as UserIcon, Phone, ArrowRight, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, roleHomePath } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@/lib/passwordPolicy';

export function RegisterPanel() {
  const { setUser } = useAuth?.() || {};
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [otpInfo, setOtpInfo] = useState(null);
  const [form, setForm] = useState({
    role: 'tenant', name: '', email: '', password: '', mobile: '',
    firm_name: '', address: '', tax_id: '', kyc_id: '', emergency_contact: '',
  });
  const [otp, setOtp] = useState('');

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(form.password)) return toast.error(PASSWORD_POLICY_MESSAGE);
    setBusy(true);
    try {
      const { data } = await api.post('/auth/register/initiate', form);
      setOtpInfo(data);
      setStep(2);
      if (data.dev_mode && data.dev_otp) {
        toast.success(`OTP (dev): ${data.dev_otp}`, { duration: 12000 });
      } else if (data.email_sent) {
        toast.success('OTP sent to your email');
      } else {
        toast.error('Could not send email — contact support');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/register/verify', { email: form.email, otp });
      localStorage.setItem('pmcrm_token', data.access_token);
      localStorage.setItem('pmcrm_user', JSON.stringify(data.user));
      document.documentElement.setAttribute('data-role', data.user.role);
      toast.success(`Welcome, ${data.user.name}! Your account is ready.`);
      window.location.href = roleHomePath(data.user.role);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post('/auth/register/resend', { email: form.email, purpose: 'verification' });
      if (data.dev_mode && data.dev_otp) toast.success(`OTP (dev): ${data.dev_otp}`, { duration: 12000 });
      else toast.success('OTP resent');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (step === 2) {
    return (
      <form onSubmit={submitOtp} className="mt-6 space-y-4">
        <div className="rounded-xl border bg-amber-50/60 px-3 py-2.5 text-xs text-amber-900">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span className="font-semibold">We sent a 6-digit code to:</span>
          </div>
          <div className="font-medium break-all mt-1">{form.email}</div>
          <div className="mt-1 text-[11px] text-amber-800/80">Enter it below to finish creating your account.</div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#0a2540] font-medium">One-time code</Label>
          <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                 inputMode="numeric" placeholder="••••••"
                 className="h-12 text-center text-2xl tracking-[12px] font-mono rounded-xl"
                 data-testid="otp-input" autoFocus />
        </div>
        <Button type="submit" disabled={busy || otp.length !== 6} className="w-full h-12 rounded-xl text-white font-semibold"
                style={{ background: 'linear-gradient(180deg, #11325c 0%, #0a2540 100%)' }} data-testid="otp-verify-button">
          {busy ? 'Verifying…' : (<>Verify &amp; Create Account <ArrowRight className="ml-1 h-4 w-4" /></>)}
        </Button>
        <div className="text-center text-sm text-slate-500">
          Didn’t get it? <button type="button" onClick={resend} className="font-medium text-[#0a2540] hover:underline" data-testid="otp-resend-button">Resend code</button> · <button type="button" onClick={() => setStep(1)} className="font-medium text-[#0a2540] hover:underline">Edit details</button>
        </div>
        {otpInfo?.dev_mode && otpInfo?.dev_otp && (
          <div className="text-xs text-center rounded-lg border bg-slate-50 p-2 text-slate-600">
            <b>Dev mode</b>: SMTP not configured. Use <span className="font-mono">{otpInfo.dev_otp}</span>
          </div>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={submitRegister} className="mt-6 space-y-3">
      <div className="space-y-1.5">
        <Label className="text-[#0a2540] font-medium">Register as <span className="text-[#d4a017]">*</span></Label>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger className="h-11 rounded-xl bg-slate-50/60 border-slate-200" data-testid="register-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="owner">Property Owner</SelectItem>
            <SelectItem value="tenant">Tenant</SelectItem>
            <SelectItem value="consultant">Property Consultant</SelectItem>
            <SelectItem value="agent">Real-estate Agent</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <Field icon={UserIcon} label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="register-name-input" placeholder="As per ID" />
        <Field icon={Mail} label="Email Address" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="register-email-input" placeholder="you@example.com" />
        <Field icon={Phone} label="Mobile" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} testId="register-mobile-input" placeholder="+91 …" />
        {form.role === 'owner' && (
          <>
            <Field icon={KeyRound} label="PAN / Tax ID (optional)" value={form.tax_id} onChange={(v) => setForm({ ...form, tax_id: v })} />
            <Field label="Address (optional)" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
          </>
        )}
        {form.role === 'consultant' && (
          <Field label="Firm name (optional)" value={form.firm_name} onChange={(v) => setForm({ ...form, firm_name: v })} />
        )}
        {form.role === 'agent' && (
          <>
            <Field label="Firm / brokerage name (optional)" value={form.firm_name} onChange={(v) => setForm({ ...form, firm_name: v })} />
            <Field icon={KeyRound} label="RERA / license number (optional)" value={form.license_number} onChange={(v) => setForm({ ...form, license_number: v })} />
          </>
        )}
        {form.role === 'tenant' && (
          <>
            <Field label="KYC ID / PAN (optional)" value={form.kyc_id} onChange={(v) => setForm({ ...form, kyc_id: v })} />
            <Field icon={Phone} label="Emergency contact (optional)" value={form.emergency_contact} onChange={(v) => setForm({ ...form, emergency_contact: v })} />
          </>
        )}
        <Field icon={Lock} label="Password" type="password" required value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="register-password-input" placeholder="At least 8 characters" />
        <PasswordRequirements value={form.password} />
      </div>
      <Button type="submit" disabled={busy} data-testid="register-submit-button"
              className="w-full h-12 rounded-xl text-white font-semibold mt-2"
              style={{ background: 'linear-gradient(180deg, #11325c 0%, #0a2540 100%)' }}>
        {busy ? 'Sending OTP…' : (<>Create Account <ArrowRight className="ml-1 h-4 w-4" /></>)}
      </Button>
      <p className="text-[11px] text-slate-500 text-center mt-2">We’ll email a 6-digit code to verify your address before activating the account.</p>
    </form>
  );
}

function Field({ icon: Icon, label, required, type = 'text', value, onChange, placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#0a2540] font-medium">{label}{required && <span className="text-[#d4a017]"> *</span>}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />}
        <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder} data-testid={testId}
               className={`h-11 rounded-xl bg-slate-50/60 border-slate-200 ${Icon ? 'pl-9' : ''}`} />
      </div>
    </div>
  );
}
